import {
  getAufnahmedatumLabel,
  getErrorMessage,
  getKundeInfoAsString,
  IAttributGruppe,
  IDienstleistung,
  IDienstleistungAbschlussInfo,
  IDienstleistungBasisDatenUpdate,
  IDienstleistungBearbeitung,
  IDienstleistungHistory,
  IDienstleistungKundeUpdate,
  IDienstleistungLeistungArtZuordnung,
  IDienstleistungResult,
  IDienstleistungUpdate,
  IGesundheitsPartnerZuordnung,
  IGesundheitsProgrammInfo,
  IKunde,
  IMessage,
  IResult,
  isKidsPrgoramm,
  istVorBearbeitungStatus
} from '@mng/mgs-lib';
import { firstValueFrom } from 'rxjs';
import { LocalStorageService } from 'ngx-webstorage';
import { computed, inject, Service, signal } from '@angular/core';
import { getFormattedDateString, normalizeName, openDownloadWithFilename } from '@mng/mng-ts-helpers';
import { MgsFieldRegistry } from '../attribute/attribut/registry/mgs-field-registry';
import { MngMessageService } from '@mng/mng-message-handler';
import { MgsActiveState } from '../mgs-active.state';
import { DienstleistungFormHandler } from './dienstleistung-form-handler';
import { MngDialogService } from '@mng/mng-inputs';
import { Router } from '@angular/router';
import { executeActionOnLogout } from '../mgs-action-on-logout';
import { handleOptimisticLockingError } from '../mgs-error-handling';
import { HttpErrorResponse } from '@angular/common/http';
import { MgsWebApi } from '../mgs-webapi.service';
import { DEMO_PATH, DIENSTLEISTUNG_PATH, DIENSTLEISTUNGEN_PATH } from '../mgs-path-defs';
import { MGS_WARNING_ICON } from '../mgs-svg-icon.helper';
import { attributGruppenInfoFuerAnzeige } from '../attribute/attribut-gruppen/attribut-gruppen-helper';
import { AuthService } from '../auth/auth.service';

const DL_MSG_KEY_KEINPARTNERPROGRAMM = 'KeinPartnerProgramm';

export type DienstleistungUpdateType = 'setzeInBearbeitung' | 'abschluss';

export interface IDienstleistungUpdateAction {
  code?: string;
  time?: string;
  action?: DienstleistungUpdateType;
}

@Service()
export class DienstleistungService {
  webApi = inject(MgsWebApi);
  private localStorage = inject(LocalStorageService);
  private messageService = inject(MngMessageService);
  private activeState = inject(MgsActiveState);
  private router = inject(Router);

  registry = new MgsFieldRegistry();

  hauptGruppe = signal<IAttributGruppe | undefined>(undefined);
  normalizedHauptGruppeLabel = computed(() =>
    this.hauptGruppe() ? this.normalizedGruppenLabel(this.hauptGruppe()) : undefined
  );

  formHandler: DienstleistungFormHandler;

  dienstleistung = signal<IDienstleistung>(undefined);

  isKids = computed(() => isKidsPrgoramm(this.dienstleistung()?.programm.id));
  vorbearbeitungStatus = computed(() => this.dienstleistung() && istVorBearbeitungStatus(this.dienstleistung()));

  aufnahmeDatumLabel = computed(() => getAufnahmedatumLabel(this.dienstleistung()?.dienstleistungsArt));

  kunde = computed(() => this.dienstleistung()?.kunde);

  kundeInfo = computed(() => getKundeInfoAsString(this.kunde(), { withKundenNr: false, withGeburtsDatum: true }));

  errorMessage = signal<IMessage | undefined>(undefined);

  hasError = computed(() => !!this.errorMessage());

  keinPartnerProgramm = computed(() => this.hasError() && DL_MSG_KEY_KEINPARTNERPROGRAMM === this.errorMessage()?.key);

  readonly dialogService = inject(MngDialogService);

  externePartnerZuordnungen = signal<IGesundheitsPartnerZuordnung[]>(undefined);

  partnerId = computed(() => this.dienstleistung().user.partner.id);

  isKidsProgramm = computed(() => isKidsPrgoramm(this.dienstleistung()?.programm.id));

  istDienstleistungInBearbeitungSetzenMoeglich = computed(
    () => this.vorbearbeitungStatus() && this.dienstleistung().anspruchsZeitraumValid && !this.hasError()
  );

  anzeigeAttributGruppenInfo = computed(() => attributGruppenInfoFuerAnzeige(this.dienstleistung()));

  aufrufAusDienstleistungen = signal(false);

  dienstleistungUpdateAction = signal<IDienstleistungUpdateAction>(undefined);

  readonly authService = inject(AuthService);

  historyStorageKey = computed(() => `dl_history_${this.authService.authState.user()?.userId}`);

  constructor() {
    executeActionOnLogout(() => this.reset());
  }

  async initDienstleistung(code: string): Promise<void> {
    this.aufrufAusDienstleistungen.set(DIENSTLEISTUNGEN_PATH === this.router.currentNavigation().extras.state?.from);
    if (!this.dienstleistung() || this.dienstleistung().code !== code) {
      const dienstleistungResult = await firstValueFrom(this.webApi.dienstleistung(code));
      this.reset();
      this.handleDienstleistungResult(dienstleistungResult);
    }
  }

  handleDienstleistungUpdate(dienstleistung: IDienstleistung, action: DienstleistungUpdateType) {
    const updateAction: IDienstleistungUpdateAction = {
      code: dienstleistung.code,
      action,
      time: new Date().toISOString()
    };
    this.dienstleistungUpdateAction.set(updateAction);
  }

  handleDienstleistungResult(dienstleistungResult: IDienstleistungResult) {
    this.dienstleistung.set(dienstleistungResult.data.result);
    this.errorMessage.set(getErrorMessage(dienstleistungResult));

    if (!this.vorbearbeitungStatus()) {
      this.activeState.updateDienstleistung(this.dienstleistung());

      if (this.formHandler) {
        this.formHandler.updateDienstleistung(this.dienstleistung());
      }

      this.bearbeitungsHistorieSpeichern(this.dienstleistung());
    }
  }

  normalizedGruppenLabel(gruppe: IAttributGruppe): string {
    return normalizeName(gruppe.label);
  }

  findAttributGruppe(gruppeParam: string): IAttributGruppe {
    return this.anzeigeAttributGruppenInfo().attributGruppen.find((gruppe) => {
      const currentGruppeParam = this.normalizedGruppenLabel(gruppe);
      return gruppeParam === currentGruppeParam;
    });
  }

  dienstleistungHistoryLesen(): IDienstleistungHistory[] {
    return this.localStorage.retrieve(this.historyStorageKey()) ?? [];
  }

  bearbeitungsHistorieSpeichern(dienstleistung: IDienstleistung) {
    const historyItem: IDienstleistungHistory = {
      code: dienstleistung.code,
      bearbeitungAm: getFormattedDateString(dienstleistung.bearbeitungsDatum),
      programmInfo: dienstleistung.programm.label,
      kundeInfo: getKundeInfoAsString(dienstleistung.kunde)
    };
    let historyItems = this.dienstleistungHistoryLesen();

    if (!historyItems.map((item) => item.code).includes(historyItem.code)) {
      historyItems = [historyItem, ...historyItems];
      if (historyItems.length > 10) {
        historyItems = historyItems.splice(10);
      }
      this.localStorage.store(this.historyStorageKey(), historyItems);
    }
  }

  updateHauptGruppe(gruppeAsString: string): IAttributGruppe {
    this.hauptGruppe.set(gruppeAsString ? this.findAttributGruppe(gruppeAsString) : undefined);
    return this.hauptGruppe();
  }

  async setzeDienstleistungInBearbeitung(bearbeitung: IDienstleistungBearbeitung) {
    const dienstleistungResult = await firstValueFrom(this.webApi.setzeDienstleistungInBearbeitung(bearbeitung));
    this.handleDienstleistungResult(dienstleistungResult);
    this.handleDienstleistungUpdate(this.dienstleistung(), 'setzeInBearbeitung');
  }

  async speichern() {
    // console.log('Speichern', this.formHandler.formGroup.getRawValue());

    // Ermittlung der Fehler (NUR für die Attribute die auch "angegriffen" wurden -> dirty)
    // ansonsten müsste bei umfangreichen Dienstleistungen "ALLES" im Context ausgefüllt werden
    const attributeMitError = this.formHandler.ermittleAttributeMitError(true);

    if (attributeMitError?.length > 0) {
      const htmlContent = this.formHandler.errorsAsHtml(attributeMitError);
      this.dialogService.showOK({
        htmlContent,
        width: '600px',
        iconName: MGS_WARNING_ICON,
        iconColor: 'error',
        title: 'Speichern ist nicht möglich: es existieren Eingabefehler'
      });
      return;
    }

    const updatedAttribute = this.formHandler.getUpdatedAttribute();

    const updatedImpfungen = this.formHandler.getUpdatedImpfungen();

    const update: IDienstleistungUpdate = {
      id: this.dienstleistung().id,
      version: this.dienstleistung().version,
      attribute: updatedAttribute,
      impfungen: this.formHandler.getUpdatedImpfungen()
    };

    try {
      const result = await firstValueFrom(this.webApi.dienstleistungSpeichern(update));

      this.dienstleistung().version = result.version;

      if (updatedImpfungen && (result.impfungen.impfungen || result.impfungen.impfungenInfo)) {
        if (result.impfungen.impfungen) {
          this.dienstleistung().impfungen.impfungen = result.impfungen.impfungen;
        }
        if (result.impfungen.impfungenInfo) {
          this.dienstleistung().impfungen.impfungenInfo = result.impfungen.impfungenInfo;
        }

        // ImpfungenUiGroup wird neu aufgebaut
        this.formHandler.updateImpfungenUi();
      }

      // Die ID's nach dem Speichern setzen
      if (result.attribute?.length > 0) {
        this.formHandler.updateAttributWerte(result.attribute);
      }

      this.formHandler.formGroup.markAsPristine({ onlySelf: false });

      void this.messageService.showMessage(
        'Daten wurden gespeichert!',
        this.messageService.createDefaultSnackbarTopRightConfig()
      );
    } catch (e) {
      if (e instanceof HttpErrorResponse) {
        handleOptimisticLockingError(this.dialogService, e);
      }
    }
  }

  async updateKunde(kunde: IKunde): Promise<IKunde> {
    const kundeUpdate: IDienstleistungKundeUpdate = { kunde, id: this.dienstleistung().id };
    const updatedDienstleistungKunde = await firstValueFrom(this.webApi.kundenAenderung(kundeUpdate));

    this.dienstleistung.update((dienstleistung) => ({
      ...dienstleistung,
      kunde: updatedDienstleistungKunde.kunde,
      version: updatedDienstleistungKunde.version,
      datenSpeichern: updatedDienstleistungKunde.kunde.datenSpeichern
    }));

    return updatedDienstleistungKunde;
  }

  async updateDienstleistungBasisdaten(dienstleistung: Partial<IDienstleistung>, arztWechsel = false): Promise<void> {
    const update: IDienstleistungBasisDatenUpdate = {
      id: this.dienstleistung().id,
      version: this.dienstleistung().version
    };

    if (arztWechsel) {
      update.arztWechsel = true;
    } else {
      update.aufnahmeDatum = dienstleistung.aufnahmeDatum ?? this.dienstleistung().aufnahmeDatum;
      update.einbettzimmerzuschlag =
        dienstleistung.einbettzimmerzuschlag ?? this.dienstleistung().einbettzimmerzuschlag;
      update.einbettzuschlaganzahl =
        dienstleistung.einbettzuschlaganzahl ?? this.dienstleistung().einbettzuschlaganzahl;
    }

    const updated = await firstValueFrom(this.webApi.dienstleistungBasisDatenAenderung(update));
    // this.dienstleistung.update((aktuelleDienstleistung) => ({ ...aktuelleDienstleistung, ...update, version: updated.version }));
    this.dienstleistung.update((aktuelleDienstleistung) => {
      const updatedDienstleistung: IDienstleistung = { ...aktuelleDienstleistung, ...update, version: updated.version };
      if (arztWechsel) {
        updatedDienstleistung.arztWechselCheckRequired = false;
        updatedDienstleistung.arzt = updatedDienstleistung.user;
      }
      return updatedDienstleistung;
    });
  }

  async arztWechsel() {
    // wird verwendet um den "Zuschlag" automatisch mit 1 click zu setzen (ohne Eingabe)
    await this.updateDienstleistungBasisdaten({}, true);
  }

  async einzelzimmerZuschlagSetzen() {
    // wird verwendet um den "Zuschlag" automatisch mit 1 click zu setzen (ohne Eingabe)
    await this.updateDienstleistungBasisdaten({
      einbettzimmerzuschlag: true,
      einbettzuschlaganzahl: this.dienstleistung().programm.aufenthaltDauer
    });
  }

  resetWennAktiv(id: number) {
    if (this.dienstleistung()?.id === id) {
      this.reset();
    }
  }

  async ermittleExternePartnerZuordnungen() {
    if (!this.externePartnerZuordnungen()) {
      const result = (
        await firstValueFrom(this.webApi.externePartnerZuordnungenFuerDienstleistung(this.dienstleistung().id))
      ).data.result;
      this.externePartnerZuordnungen.set(result);
    }
    return this.externePartnerZuordnungen;
  }

  async updateExternePartnerZuordnungen(zuordnungen: IDienstleistungLeistungArtZuordnung[]) {
    const result: IDienstleistungLeistungArtZuordnung[] = (
      await firstValueFrom(this.webApi.updateExterneLeistungArtZuordnungen(zuordnungen))
    ).data.result;
    this.dienstleistung.update((dienstleistung) => ({ ...dienstleistung, externeZuordnungen: result }));
  }

  reset() {
    this.dienstleistung.set(undefined);
    this.formHandler = undefined;
    this.externePartnerZuordnungen.set(undefined);
    this.hauptGruppe.set(undefined);
  }

  updateFormHandler(formHandler: DienstleistungFormHandler) {
    this.formHandler = formHandler;
  }

  async dienstleistungAbschlussInfo(): Promise<IResult<IDienstleistungAbschlussInfo>> {
    const result = await firstValueFrom(this.webApi.dienstleistungAbschlussInfo(this.dienstleistung().id));
    return result;
  }

  async abschluss() {
    await firstValueFrom(this.webApi.dienstleistungAbschluss(this.dienstleistung().id));
    this.handleDienstleistungUpdate(this.dienstleistung(), 'abschluss');
    void this.messageService.showMessage(
      `Die Dienstleistung mit dem Code ${this.dienstleistung().code} wurde abgeschlossen`
    );

    if (this.isKidsProgramm()) {
      // Bei den Kids wird die Dienstleistung immer von der Termin Ansicht aus gestartet -> dort wird einfach "hin" zurück navigiert
      window.history.back();
    } else if (this.dienstleistung().demoDienstleistung) {
      await this.router.navigateByUrl('/' + DEMO_PATH, { replaceUrl: true });
    } else {
      if (this.aufrufAusDienstleistungen()) {
        await this.router.navigateByUrl('/' + DIENSTLEISTUNGEN_PATH, { replaceUrl: true });
      } else {
        await this.router.navigateByUrl('/' + DIENSTLEISTUNG_PATH, { replaceUrl: true });
      }
    }
    this.reset();
  }
  async demoDienstleistung(programm: IGesundheitsProgrammInfo) {
    const dienstleistungResult = await firstValueFrom(this.webApi.demoDienstleistung(programm.id));
    this.handleDienstleistungResult(dienstleistungResult);
    void this.router.navigate([DIENSTLEISTUNG_PATH, dienstleistungResult.data.result.code]);
  }

  async untersuchungsVergleichPdfAnzeige(attributDefId: number) {
    try {
      const objectUrl = await this.webApi.untersuchungsVergleichPdf(this.dienstleistung().id, attributDefId);
      openDownloadWithFilename(objectUrl.filename, objectUrl.blob);
    } catch (e) {
      this.dialogService.showOK({
        htmlContent: 'Bei der Ermittlung des Untersuchungsvergleich ist ein Fehler aufgetreten!',
        iconColor: 'error',
        iconName: 'warning'
      });
    }
  }
}
